import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Stat, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getReferrals, submitReferral, type ReferralDto } from '@/data/client';

/* Referral Rewards: a client refers a business to Triton and earns a credit
   once the business meets with Triton. Every login can refer. The page is the
   form plus the record of what was sent and what it earned. */
const STATUS_VARIANT: Record<string, 'neutral' | 'accent' | 'success' | 'warning' | 'outline'> = { received: 'neutral', contacted: 'accent', qualified: 'success', not_fit: 'outline', existing_client: 'outline' };
const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export function ReferralRewards() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['referrals'], queryFn: getReferrals });
  const [open, setOpen] = useState(false);
  const rate = data?.rate ?? 100;
  const rows = data?.referrals ?? [];
  const t = data?.totals;

  return (
    <div className="dash-stack">
      <Card padding="lg" className="rr-hero">
        <div className="rr-hero__text">
          <h2>Know a business that could use Triton?</h2>
          <p>Refer them. Once they meet with Triton, your account earns a <strong>{money(rate)}</strong> credit, refunded against your previous billing. There is no limit.</p>
        </div>
        {!open && <Button onClick={() => setOpen(true)} iconLeft={<Icon name="plus" size={15} />}>Refer a business</Button>}
      </Card>

      {open && <ReferralForm rate={rate} onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['referrals'] }); }} onCancel={() => setOpen(false)} />}

      <div className="lead-mini lead-mini--4">
        <Card padding="lg" className="lead-mini__card"><Stat label="Referrals sent" value={t?.sent ?? 0} sub="all time" /></Card>
        <Card padding="lg" className="lead-mini__card"><Stat label="Qualified" value={t?.qualified ?? 0} sub="met with Triton" /></Card>
        <Card padding="lg" className="lead-mini__card"><Stat label="Credits earned" value={money(t?.earned ?? 0)} sub={t?.pending ? `${money(t.pending)} not yet issued` : 'all issued'} /></Card>
        <Card padding="lg" className="lead-mini__card"><Stat label="Credits issued" value={money(t?.issued ?? 0)} sub="refunded to you" /></Card>
      </div>

      <Card padding="lg">
        <h2 className="dash-card__title">Your referrals</h2>
        {isLoading ? <p className="dash-muted">Loading…</p> : rows.length === 0 ? <p className="dash-muted">Nothing sent yet. Your first referral is one form away.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="dash-table">
              <thead><tr><th>Business</th><th>Sent</th><th>By</th><th>Status</th><th>Credit</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.business}</strong>{r.contactName && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.contactName}</div>}</td>
                    <td className="dash-muted" style={{ whiteSpace: 'nowrap' }}>{r.submittedAt.slice(0, 10)}</td>
                    <td className="dash-muted">{r.submittedBy}</td>
                    <td><Badge variant={STATUS_VARIANT[r.status] ?? 'neutral'} dot={r.status === 'qualified'}>{r.statusLabel}</Badge>{r.status === 'not_fit' && r.statusNote && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.statusNote}</div>}</td>
                    <td>{creditCell(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="dash-muted" style={{ fontSize: 'var(--text-xs)', marginTop: 10 }}>A referral qualifies when the business is in Triton&rsquo;s industries, knows Triton is calling, and agrees to a meeting. Credits are refunded against your previous billing; &ldquo;issued&rdquo; is Triton&rsquo;s confirmation that the refund went out.</p>
      </Card>
    </div>
  );
}

function creditCell(r: ReferralDto) {
  if (r.status !== 'qualified' || r.creditAmount == null) return <span className="dash-muted">—</span>;
  return r.creditIssuedOn
    ? <span><strong>{money(r.creditAmount)}</strong><div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Issued {r.creditIssuedOn}{r.creditIssuedNote ? ` · ${r.creditIssuedNote}` : ''}</div></span>
    : <span><strong>{money(r.creditAmount)}</strong><div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Earned · not yet issued</div></span>;
}

function ReferralForm({ rate, onDone, onCancel }: { rate: number; onDone: () => void; onCancel: () => void }) {
  const [f, setF] = useState({ business: '', contactName: '', phone: '', email: '', website: '', notes: '', consent: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => setF({ ...f, [k]: v });
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await submitReferral(f); setDone(true); setTimeout(onDone, 1800); }
    catch (e) { setErr(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/^\{"error":"(.*)"\}$/, '$1') : 'Could not send.'); }
    finally { setBusy(false); }
  };
  if (done) return <Card padding="lg"><p style={{ margin: 0 }}><Icon name="check" size={16} /> Sent. Triton will reach out to {f.business}, and you&rsquo;ll see its status here.</p></Card>;
  return (
    <Card padding="lg">
      <h2 className="dash-card__title">Refer a business</h2>
      <div className="rr-form">
        <Input label="Business name" value={f.business} onChange={(e) => set('business', e.target.value)} required autoFocus />
        <Input label="Contact person" value={f.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Who Triton should ask for" />
        <Input label="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(651) 555-0100" />
        <Input label="Email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="name@business.com" />
        <Input label="Website" value={f.website} onChange={(e) => set('website', e.target.value)} placeholder="business.com" />
        <label className="tr-field rr-form__wide"><span className="tr-field__label">What do they need?</span><Textarea rows={3} value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder="A new website, more leads, help with their ads…" /></label>
        <label className="rr-form__consent rr-form__wide">
          <input type="checkbox" checked={f.consent} onChange={(e) => set('consent', e.target.checked)} />
          <span>They know Triton will call and have agreed to a meeting.</span>
        </label>
        <div className="rr-form__wide" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button disabled={busy || !f.business.trim() || !f.consent} onClick={submit}>{busy ? 'Sending…' : 'Send referral'}</Button>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
          {err && <span className="admin-modal__err">{err}</span>}
        </div>
        <p className="dash-muted rr-form__wide" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>Once Triton confirms the referral, {money(rate)} is refunded against your previous billing.</p>
      </div>
    </Card>
  );
}
